import http from "./http";
import { API_URL, USE_MOCK } from "./config";
import { buildSeed } from "./mock/resources";
import { getPath } from "../utils/object";

// Generic CRUD client for the config-driven pages (pages/Resource/resources.js).
// Live mode follows tm-admin-web-react's REST shape:
//   GET    {API}{endpoint}?page&limit&...  -> payload: { data: [], meta: { totalPages, total } }
//   GET    {API}{endpoint}/:id             -> payload: {...}
//   POST   {API}{endpoint}   PATCH {API}{endpoint}/:id   DELETE {API}{endpoint}/:id
//   POST   {API}{endpoint}/<upload.path>/:id  (multipart, field "file")
// Mock mode keeps rows in localStorage so created/edited rows survive a reload.

const url = (resource) => `${API_URL}${resource.endpoint}`;
// Reads can use a query-style endpoint (vendor-account-ledgers/findByVendorId); writes go to `writeEndpoint`
const writeUrl = (resource) => `${API_URL}${resource.writeEndpoint || resource.endpoint}`;

// Resources whose single-row calls use other paths than the list (customers: users/get-customer/:id,
// users/create-customer, users/update-customer/:id, users/remove-customer/:id)
const rowUrl = (resource, kind, id) => {
  const base = resource[`${kind}Endpoint`];
  if (!base) return id === undefined ? writeUrl(resource) : `${writeUrl(resource)}/${id}`;
  return id === undefined ? `${API_URL}${base}` : `${API_URL}${base}/${id}`;
};

/* ------------------------------ mock store ------------------------------ */

const cache = {};
const storageKey = (resource) => `dm-mock:${resource.slug}`;

const readRows = (resource) => {
  if (cache[resource.slug]) return cache[resource.slug];

  let rows;
  try {
    const raw = localStorage.getItem(storageKey(resource));
    if (raw) rows = JSON.parse(raw);
  } catch {
    // unreadable storage - fall back to the seed
  }
  cache[resource.slug] = rows || buildSeed(resource);
  return cache[resource.slug];
};

const writeRows = (resource, rows) => {
  cache[resource.slug] = rows;
  try {
    localStorage.setItem(storageKey(resource), JSON.stringify(rows));
  } catch {
    // storage full/unavailable - changes live for this session only
  }
};

const delay = (value) =>
  new Promise((resolve) => setTimeout(() => resolve(value), 250));

// Query params that narrow rows by equality (e.g. { currencyId: 3 }), skipping
// paging/search/date keys handled separately
const RESERVED = new Set(["page", "limit", "search", "searchString", "from", "to", "fromDate", "toDate"]);

const mockList = (resource, params) => {
  const { page = 1, limit = 10, search, searchString, fromDate, toDate, from, to } = params;
  let rows = readRows(resource);

  Object.entries({ ...resource.listParams, ...params }).forEach(([key, value]) => {
    if (RESERVED.has(key) || value === undefined || value === "") return;
    const custom = resource.mockFilters?.[key];
    if (custom) {
      rows = rows.filter((row) => custom(row, value));
      return;
    }
    rows = rows.filter((row) => row[key] === undefined || String(row[key]) === String(value));
  });

  if (resource.clientFilter) rows = rows.filter(resource.clientFilter);

  const needle = String(search || searchString || "").toLowerCase();
  if (needle) {
    rows = rows.filter((row) => JSON.stringify(row).toLowerCase().includes(needle));
  }

  const dateKey = resource.dateFilter;
  const start = from || fromDate;
  const end = to || toDate;
  if (dateKey && (start || end)) {
    rows = rows.filter((row) => {
      const day = String(getPath(row, dateKey) || "").slice(0, 10);
      return (!start || day >= start) && (!end || day <= end);
    });
  }

  const total = rows.length;
  const offset = (page - 1) * limit;
  return {
    data: rows.slice(offset, offset + limit),
    meta: { total, totalPages: Math.max(1, Math.ceil(total / limit)) },
  };
};

/* --------------------------------- API --------------------------------- */

export async function listResource(resource, params = {}) {
  if (USE_MOCK) return delay(mockList(resource, params));

  const { data } = await http.get(url(resource), {
    params: { ...resource.listParams, ...params },
  });
  const payload = data.payload;
  let rows = Array.isArray(payload) ? payload : payload?.data || [];
  const meta = (!Array.isArray(payload) && payload?.meta) || {};
  if (resource.clientFilter) rows = rows.filter(resource.clientFilter);
  return {
    data: rows,
    meta: {
      total: meta.total ?? meta.totalItems ?? meta.totalRecords ?? rows.length,
      totalPages: meta.totalPages ?? 1,
    },
  };
}

export async function getResource(resource, id) {
  if (USE_MOCK) {
    const row = readRows(resource).find((item) => String(item.id) === String(id));
    if (!row) throw new Error(`${resource.title} not found`);
    return delay(row);
  }

  const { data } = await http.get(rowUrl(resource, "get", id));
  return data.payload;
}

// -> saved row (live: the API payload). Extras (e.g. { type: "Income" }) are
// merged in last, like tm's `{ ...formData, type: "Income" }`.
export async function saveResource(resource, values) {
  const body = { ...values, ...resource.submitExtras };

  if (USE_MOCK) {
    const rows = readRows(resource);
    if (body.id) {
      const before = rows.find((row) => row.id === body.id);
      const next = rows.map((row) => (row.id === body.id ? { ...row, ...body } : row));
      writeRows(resource, next);
      if (resource.slug === "vendor") await (await import("./vendor")).applyOpeningBalances(body, before);
      return delay(body);
    }
    const nextId = rows.reduce((max, row) => Math.max(max, +row.id || 0), 0) + 1;
    const created = {
      ...body,
      id: nextId,
      createdDateTime: body.createdDateTime || new Date().toISOString(),
      isActive: body.isActive ?? true,
    };
    writeRows(resource, [created, ...rows]);
    // the real API turns a vendor's opening balances into wallet + ledger entries; mirror that here
    if (resource.slug === "vendor") await (await import("./vendor")).applyOpeningBalances(created);
    return delay(created);
  }

  const { data } = body.id
    ? await http.patch(rowUrl(resource, "update", body.id), body)
    : await http.post(rowUrl(resource, "create"), body);
  return data.payload;
}

export async function deleteResource(resource, id) {
  if (USE_MOCK) {
    writeRows(
      resource,
      readRows(resource).filter((row) => row.id !== id)
    );
    return delay(true);
  }

  await http.delete(rowUrl(resource, "delete", id));
  return true;
}

// Scan copy / logo / attachment upload that follows a save (tm: POST
// {endpoint}/upload-xyz/:id with FormData "file")
export async function uploadResourceFile(resource, uploadPath, id, file) {
  if (USE_MOCK) return delay(true);

  const formData = new FormData();
  formData.append("file", file);
  const { data } = await http.post(`${writeUrl(resource)}/${uploadPath}/${id}`, formData);
  return data?.payload ?? true;
}

// Run an arbitrary POST under the resource endpoint (e.g. loan disburse)
export async function postResourceAction(resource, path, body) {
  if (USE_MOCK) return delay({ id: Date.now(), ledger: { id: Date.now() }, ...body });

  const { data } = await http.post(`${writeUrl(resource)}/${path}`, body);
  return data.payload;
}

// {METHOD} {endpoint}/{path}/:id (tm: PATCH loans/approve/:id, PUT user-deposit-request/approve/:id)
// Mock mode applies `body` to the row (e.g. { requestStatus: "REJECTED" }).
export async function actionResource(resource, method, path, id, body = {}) {
  if (USE_MOCK) {
    const rows = readRows(resource);
    writeRows(
      resource,
      rows.map((row) => (row.id === id ? { ...row, ...body, ...(path === "approve" && !body.requestStatus && { status: "Approved" }) } : row))
    );
    return delay(true);
  }

  await http[method](`${writeUrl(resource)}/${path}/${id}`, body);
  return true;
}

// PATCH {endpoint}/:id with exactly `body` (no submitExtras) - tm's ledger
// "Edit Transaction" sends { id, createdDateTime, narration, remarks }.
// `base` overrides the endpoint (e.g. company-account-ledger).
export async function patchResource(resource, id, body, base) {
  if (USE_MOCK) {
    const rows = readRows(resource);
    writeRows(resource, rows.map((row) => (row.id === id ? { ...row, ...body } : row)));
    return delay(true);
  }

  await http.patch(base ? `${API_URL}${base}/${id}` : rowUrl(resource, "update", id), body);
  return true;
}

// PATCH {base}/{path}/:id, path defaults to update-balance (tm ledger "Edit Balance"; vendors use vendor-balance/update-ledger-balance)
export async function updateLedgerBalance(resource, id, body, base, path = "update-balance") {
  if (USE_MOCK) {
    const rows = readRows(resource);
    writeRows(
      resource,
      rows.map((row) => {
        if (row.id !== id) return row;
        const { amount, notes, ...rest } = body;
        const next = { ...row, ...rest, remarks: notes ?? row.remarks };
        if (amount !== undefined) next[row.debit ? "debit" : "credit"] = amount;
        return next;
      })
    );
    return delay(true);
  }

  await http.patch(`${API_URL}${base || resource.writeEndpoint || resource.endpoint}/${path}/${id}`, body);
  return true;
}

// Any other call (tm: POST bkash/refund-payment, ...) - `path` is relative to API_URL
export async function callEndpoint(method, path, body) {
  if (USE_MOCK) return delay({ success: true });

  const { data } = await http[method](`${API_URL}${path}`, body);
  return data;
}
